import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import logo from "../../assets/images/logo-drsaude.png";
import mobileFirstStep from "../../assets/images/first-step-mobile.png";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HttpsIcon from "@mui/icons-material/Https";
import { useStyles } from "./styles";
import { brazilianStates, emailRegex } from "../../utils/constants";
import theme from "../../theme";
import Slide from "@material-ui/core/Slide";
import { Paper } from "@material-ui/core";
import { DesktopInformations } from "../../components/DesktopInformations";
import { RequestedSummary } from "../../components/RequestedSummary";
import { useNavigate, useLocation } from "react-router-dom";
import { CpfMask, PhoneMask, dateMask, formatCEP } from "../../utils/masks";

interface ActivationProps {
  paymentType?: string;
}

const Activation = ({ paymentType }: ActivationProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { data?: any; manager_code?: string };
  const [disabled, setDisabled] = useState(true);
  const [openSummary, setOpenSummary] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const searchParams = new URLSearchParams(window.location.search);
  const slug = searchParams.get("gestor");

  const [body, setBody] = useState({
    name: "",
    birthday: "",
    cpf: "",
    phone: "",
    email: "",
    address: {
      zip_code: "",
      state: "",
      city: "",
      street: "",
      number: "",
      neighborhood: "",
      complement: "",
    },
  });

  useEffect(() => {
    if (state?.data) {
      setBody({
        name: state.data.name || "",
        birthday: state.data.birthday || "",
        cpf: state.data.cpf || "",
        phone: state.data.phone || "",
        email: state.data.email || "",
        address: {
          zip_code: state.data.address?.zip_code || "",
          state: state.data.address?.state || "",
          city: state.data.address?.city || "",
          street: state.data.address?.street || "",
          number: state.data.address?.number || "",
          neighborhood: state.data.address?.neighborhood || "",
          complement: state.data.address?.complement || "",
        },
      });
    }
  }, [state]);

  const pathname = location.pathname;
  const pathSegments = pathname.split("/").filter((segment) => segment !== "");
  const firstSegment = pathSegments[0];

  const getPaymentType = () => {
    switch (firstSegment) {
      case "cpfl":
        return "cpfl";
      case "boleto":
        return "boleto";
      case "pix-automatico":
        return "pix_automatico";
      case "promocional":
        return "credit_card";
      case "black-friday":
        return "credit_card";
      case "aniversario":
        return "credit_card";
      default:
        return paymentType || null;
    }
  };

  const getLinkType = () => {
    if (firstSegment === "black-friday") {
      return "1";
    }
    if (firstSegment === "aniversario") {
      return "2";
    }
    return null;
  };

  async function handleSubmit() {
    navigate("/registro", {
      state: {
        data: body,
        manager_code: slug,
        promotional: firstSegment === "promocional" ? true : false,
        linkType: getLinkType(),
        paymentType: getPaymentType(),
      },
    });
  }

  const searchPostalCode = async () => {
    if (
      body?.address?.zip_code &&
      body?.address?.zip_code.replace(/\D/g, "").length === 8
    ) {
      const cleanZipCode = body?.address?.zip_code.replace(/\D/g, "");
      fetch(`https://viacep.com.br/ws/${cleanZipCode}/json/`)
        .then((response) => response.json())
        .then((data) => {
          if (data.erro) return;
          setBody((prev: any) => ({
            ...prev,
            address: {
              ...prev.address,
              zip_code: data.cep,
              street: data.logradouro || "",
              neighborhood: data.bairro || "",
              city: data.localidade || "",
              state: data.uf || "",
            },
          }));
        })
        .catch((error) => {
          console.error("Erro ao buscar CEP:", error);
        });
    }
  };

  function hasEmptyFields(data: any) {
    for (const key in data) {
      if (typeof data[key] === "string" && data[key].trim() === "") {
        return true;
      }
    }
    return false;
  }

  const validateEmail = (email: string) => {
    return emailRegex.test(email);
  };
  const isDataCompleted = !hasEmptyFields(body);
  const isAddressCompleted = !hasEmptyFields(body?.address);

  useEffect(() => {
    const isEmailValid = body.email ? validateEmail(body.email) : false;
    if (
      isDataCompleted === true &&
      isAddressCompleted === true &&
      isEmailValid
    ) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [isDataCompleted, isAddressCompleted, body.email]);

  useEffect(() => {
    searchPostalCode();
  }, [body.address?.zip_code]);

  useEffect(() => {
    localStorage.removeItem("selectedDaysBoleto");
  }, []);

  return (
    <>
      {openSummary ? (
        <Slide
          direction="right"
          in
          mountOnEnter
          unmountOnExit
        >
          <Paper elevation={4}>
            <RequestedSummary
              firstSegment={firstSegment === "promocional" ? "promocional" : undefined}
              setOpenSummary={setOpenSummary}
              holderName={body.name}
              holderCpf={body.cpf}
              holderPhone={body.phone}
              holderEmail={body.email}
              holderAddress={body.address.street}
              holderCep={body.address.zip_code}
              paymentType={getPaymentType() ?? undefined}
              linkType={getLinkType()}
            />
          </Paper>
        </Slide>
      ) : (
        <Box className={classes.container}>
          <Box className={classes.header}>
            <Box className={classes.wrapperInfo}>
              <Box className={classes.containerSteps}>
                <Box className={classes.containerCircle}>
                  <Box className={classes.circle}>
                    <Typography className={classes.circleText}>1</Typography>
                  </Box>
                  <Typography className={classes.textStep}>Cadastro</Typography>
                </Box>
                <Box className={classes.containerCircle}>
                  <Box className={classes.circleOutlined}>
                    <Typography className={classes.circleTextOutlined}>
                      2
                    </Typography>
                  </Box>
                  <Typography
                    className={classes.textStep}
                    style={{ color: "#908F8F", fontWeight: 500 }}
                  >
                    Forma de pagamento
                  </Typography>
                </Box>
                <Box className={classes.containerCircle}>
                  <Box className={classes.circleOutlined}>
                    <Typography className={classes.circleTextOutlined}>
                      3
                    </Typography>
                  </Box>
                  <Typography
                    className={classes.textStep}
                    style={{ color: "#908F8F", fontWeight: 500 }}
                  >
                    Comprovante de ativação
                  </Typography>
                </Box>
              </Box>
              <img
                src={logo}
                alt="Logo Dr Saude"
                className={classes.logo}
              />
              <Box
                className={classes.retangleOrder}
                onClick={() => setOpenSummary(true)}
              >
                <Typography className={classes.textOrder}>
                  Resumo do pedido
                </Typography>
                <KeyboardArrowDownIcon
                  style={{ color: theme.palette.text.primary }}
                />
              </Box>
            </Box>
            <Box className={classes.containerEnvinroment}>
              <Typography className={classes.textSecurity}>
                <HttpsIcon
                  style={{ width: "16px", color: theme.palette.text.secondary }}
                />
                ambiente seguro
              </Typography>
            </Box>
            <Box className={classes.wrapperStep}>
              <img
                src={mobileFirstStep}
                alt="Etapa da tela"
              />
            </Box>
          </Box>
          <Typography className={classes.title}>
            Ativação do cartão de benefícios
          </Typography>
          <Box className={classes.containerColumns}>
            <Box className={classes.box}>
              <Typography className={classes.boxTitle}>
                Informação do titular principal
              </Typography>
              <Box className={classes.separator} />
              <>
                <label className={classes.label}>Nome completo</label>
                <TextField
                  name="name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: "5px", marginBottom: "20px" }}
                  onChange={(e) => setBody({ ...body, name: e.target.value })}
                  value={body.name}
                />
                <Box className={classes.divRow}>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Data nascimento</label>
                    <TextField
                      name="birthday"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({ ...body, birthday: e.target.value })
                      }
                      value={dateMask(body.birthday) ?? body.birthday}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Box>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>CPF</label>
                    <TextField
                      name="cpf"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({ ...body, cpf: e.target.value })
                      }
                      value={CpfMask(body.cpf) ?? body.cpf}
                      inputProps={{ maxLength: 14 }}
                    />
                  </Box>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Telefone de contato</label>
                    <TextField
                      name="phone"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({ ...body, phone: e.target.value })
                      }
                      value={PhoneMask(body.phone) ?? body.phone}
                    />
                  </Box>
                </Box>
                <label className={classes.label}>E-mail</label>
                <TextField
                  name="email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: "5px", marginBottom: "20px" }}
                  onChange={(e) => {
                    const emailValue = e.target.value;
                    setBody({ ...body, email: emailValue });

                    if (emailValue && !validateEmail(emailValue)) {
                      setEmailError("Por favor, insira um e-mail válido");
                    } else {
                      setEmailError("");
                    }
                  }}
                  value={body.email}
                  error={!!emailError}
                  helperText={emailError}
                  type="email"
                />
                <label className={classes.label}>CEP</label>
                <TextField
                  name="cep"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: "5px", marginBottom: "20px" }}
                  onChange={(e) => {
                    const value = e.target.value;
                    const formattedValue = formatCEP(value);
                    setBody({
                      ...body,
                      address: { ...body.address, zip_code: formattedValue },
                    });
                  }}
                  value={body.address.zip_code}
                  inputProps={{ maxLength: 9 }}
                />
                <label className={classes.label}>Endereço</label>
                <TextField
                  name="address"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ marginTop: "5px", marginBottom: "20px" }}
                  onChange={(e) =>
                    setBody({
                      ...body,
                      address: { ...body.address, street: e.target.value },
                    })
                  }
                  value={body.address.street}
                />
                <Box className={classes.divRow}>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Bairro</label>
                    <TextField
                      name="neighborhood"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({
                          ...body,
                          address: {
                            ...body.address,
                            neighborhood: e.target.value,
                          },
                        })
                      }
                      value={body.address.neighborhood}
                    />
                  </Box>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Número</label>
                    <TextField
                      name="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({
                          ...body,
                          address: { ...body.address, number: e.target.value },
                        })
                      }
                      value={body.address.number}
                    />
                  </Box>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Complemento</label>
                    <TextField
                      name="complement"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({
                          ...body,
                          address: {
                            ...body.address,
                            complement: e.target.value,
                          },
                        })
                      }
                      value={body.address.complement}
                    />
                  </Box>
                </Box>
                <Box className={classes.divRow}>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Cidade</label>
                    <TextField
                      name="city"
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                      onChange={(e) =>
                        setBody({
                          ...body,
                          address: { ...body.address, city: e.target.value },
                        })
                      }
                      value={body.address.city}
                    />
                  </Box>
                  <Box className={classes.labelAndInput}>
                    <label className={classes.label}>Estado</label>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{ marginTop: "5px", marginBottom: "20px" }}
                    >
                      <Select
                        value={body.address.state}
                        onChange={(e) =>
                          setBody({
                            ...body,
                            address: { ...body.address, state: e.target.value },
                          })
                        }
                        displayEmpty
                        variant="outlined"
                      >
                        <MenuItem
                          value=""
                          disabled
                        >
                          Selecione um estado
                        </MenuItem>
                        {brazilianStates.map((state) => (
                          <MenuItem
                            key={state.code}
                            value={state.code}
                          >
                            {state.code} - {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Box className={classes.containerButton}>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={disabled}
                    sx={{
                      backgroundColor: theme.palette.primary.dark,
                      color: "#FFF",
                      fontSize: "18px",
                      marginTop: {
                        xs: "0px",
                        md: "30px",
                      },
                    }}
                    className={classes.button}
                  >
                    Continuar
                  </Button>
                </Box>
              </>
            </Box>
            <DesktopInformations
              firstSegment={firstSegment === "promocional" ? "promocional" : undefined}
              setOpenSummary={setOpenSummary}
              holderName={body.name}
              holderCpf={body.cpf}
              holderPhone={body.phone}
              holderEmail={body.email}
              holderAddress={body.address.street}
              holderCep={body.address.zip_code}
              paymentType={getPaymentType() ?? undefined}
              linkType={getLinkType()}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default Activation;
